import { getApolloAuthClient } from '@faustwp/core';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { useGetArticlesQuery } from './hooks/useGetArticlesQuery';
import { useGetArticleTotals } from './hooks/useGetArticleTotals';
import { getCategoryLookup } from './helpers';
import {
  FeaturedArticlesFragmentFragment,
  GetBlogQuery,
  Page,
} from '../../../__generated__/graphql';
import ArticleCard from '../../../components/ArticleCard';
import Button from '../../../components/Button';
import IconButton from '../../../components/IconButton';
import Caret from '../../../components/icons/Caret';
import { Paragraph } from '../../../components/Text';
import BlogQuery from '../../../pages/about-us/blog/blogQuery.gql';
import { useRouterParams } from '../../../utils/useRouterParams';
import NewsletterSignupCard from '../../NewsletterSignup/NewsletterSignupCard';
import { searchId } from '../FilterBar';

import style from './articles.module.css';

interface Props {
  featuredArticles: FeaturedArticlesFragmentFragment[];
}

const pageSize = 6;

const Articles: React.FC<Props> = ({ featuredArticles }) => {
  const [didMount, setDidMount] = useState(false);

  const blogQuery = getApolloAuthClient().readQuery<GetBlogQuery>({
    query: BlogQuery,
  });

  const router = useRouter();

  const routerParams = useRouterParams();
  const {
    categoryId,
    cursors: paramsCursors = '[]',
    search,
    tagId,
  } = routerParams;

  const cursors = JSON.parse(paramsCursors) as string[];
  const parsedCategoryId = !!categoryId && !Array.isArray(categoryId) ? parseInt(categoryId) : null;
  const parsedTagId = !!tagId && !Array.isArray(tagId) ? tagId : null;

  const categoryLookup = getCategoryLookup(featuredArticles);

  const articlesData = useGetArticlesQuery({
    after: cursors[cursors.length - 1] ?? null,
    categoryId: parsedCategoryId,
    first: pageSize,
    notIn: categoryLookup[parsedCategoryId ?? 0],
    search: search ?? undefined,
    tagId: parsedTagId,
  });

  // warm up the previous page in case the user is visiting page 2 or higher on initial load
  useGetArticlesQuery({
    after: cursors[cursors.length - 2] ?? null,
    categoryId: parsedCategoryId,
    first: pageSize,
    notIn: categoryLookup[parsedCategoryId ?? 0],
    search: search ?? undefined,
    tagId: parsedTagId,
  });

  const totalsData = useGetArticleTotals({
    categoryId: parsedCategoryId,
    notIn: categoryLookup[parsedCategoryId ?? 0],
    search: search ?? undefined,
    tagId: parsedTagId,
  });

  const pageInfo = articlesData.data?.posts?.pageInfo;

  const handleSetCursors = (newCursors: string[], scrollToFilterBar: boolean) => {
    void router.replace({
      pathname: router.pathname,
      query: {
        ...routerParams,
        cursors: JSON.stringify(newCursors),
      },
    }, undefined, {
      scroll: false,
    }).then(() => {
      if (!scrollToFilterBar) {
        return;
      }

      const searchInput = document.getElementById(searchId);

      if (!searchInput) {
        return;
      }

      searchInput.scrollIntoView({
        behavior: 'instant',
      });
    });
  };

  const handleFetchPrevious = () => {
    if (pageInfo?.hasPreviousPage) {
      if (cursors.length > 1) {
        handleSetCursors(cursors.slice(0, -1), true);
      }
      else {
        handleSetCursors([], true);
      }
    }
  };

  const handleFetchMore = () => {
    if (pageInfo?.hasNextPage) {
      if (pageInfo.endCursor) {
        const endCursor = pageInfo.endCursor;
        handleSetCursors([...cursors, endCursor], true);
      }
    }
  };

  useEffect(() => {
    setDidMount(true);
  }, []);

  useEffect(() => {
    if (didMount) {
      handleSetCursors([], false);
    }
    // Reset cursors when category, search, or tag changes
    // We don't want to reset cursors on initial mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, search, tagId]);

  const blogIndexContent = (blogQuery?.nodeByUri as Page).blogIndexContent;

  const totalArticles = totalsData.data?.posts?.pageInfo.total;
  const articlesRemaining = articlesData.data?.posts?.pageInfo.total;
  const pagesRemaining = Math.ceil((articlesRemaining ?? totalArticles ?? 1) / pageSize);
  const totalPages = Math.ceil((totalArticles ?? 1) / pageSize);
  const currentPage = (totalPages - pagesRemaining) + 1;

  return (
    <div>
      <div
        className={style.articles}
        data-loading={articlesData.loading}
        data-not-on-first-page={cursors.length > 0}
      >
        {articlesData.data?.posts?.nodes.map(post => (
          <ArticleCard
            article={post}
            articleLinkText="Read More"
            key={post.id}
            size="medium"
          />
        ))}

        {blogIndexContent?.newsletter && (
          <div className={style.newsletterContainer}>
            <NewsletterSignupCard
              darkBackground={false}
              description={blogIndexContent.newsletter.description ?? ''}
              marketoFormId={blogIndexContent.newsletter.marketoFormId}
              successMessage={blogIndexContent.newsletter.successMessage ?? ''}
              title={blogIndexContent.newsletter.title ?? ''}
            />

          </div>
        )}
      </div>

      {pageInfo && totalPages > 0 && (
        <div className={style.paginationButtons}>
          <Paragraph
            fontSize={12}
            fontWeight={700}
          >
            {`Page ${currentPage.toString()} of ${totalPages.toString()}`}
          </Paragraph>

          {totalPages > 1 && (
            <>
              <IconButton
                aria-label="Previous page"
                className={style.previousButton}
                disabled={!pageInfo.hasPreviousPage}
                onClick={handleFetchPrevious}
                round
              >
                <Caret />
              </IconButton>

              <Button
                buttonColor="black"
                disabled={!pageInfo.hasNextPage}
                onClick={handleFetchMore}
              >
                Next
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Articles;
